import {
  Alert,
  Button,
  Card,
  Label,
  Table,
  TextInput,
  Timeline,
} from "flowbite-react";
import React, { useEffect, useRef, useState } from "react";
import { CgSpinner } from "react-icons/cg";
import { FaSearch } from "react-icons/fa";
import { isValidCPF } from "../../utils/utils";
import axios from "../../config/axiosInstance";
import { QUERIES } from "../../config/apiConfig";
import { maskCPF } from "../../utils/masks";
import { HiInformationCircle } from "react-icons/hi";
import HeaderQuery from "./HeaderQuery";
import CardQueryDescription from "../common/CardQueryDescription";
import QueryLoading from "../common/QueryLoading";
import TitleStripe from "../common/TitleStripe";
import Enderecos from "../common/query/Enderecos";
import Emails from "../common/query/Emails";
import Telefones from "../common/query/Telefones";
import NoOcurrence from "../common/query/NoOcurrence";
import usePDF from "../../hooks/usePDF";

const CadastralPFQuery = ({ query, selectedPrice }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");
  const [errorQuery, setErrorQuery] = useState("");
  const [data, setData] = useState(null);
  const [errorData, setErrorData] = useState("");
  const componentRef = useRef();

  const { handleClickDownloadPDF, pdfURL, setPdfURL } = usePDF(
    componentRef,
    info,
    query?.id,
    data
  );

  const handleChange = (event) => {
    let valor = event.target.value.replace(/\D/g, "");

    valor = maskCPF(valor);

    setInfo(valor);
  };

  const fetchInfo = async (raw) => {
    return await axios(
      `${QUERIES.GET_V2_QUERY}/cadastral-pf-quod/${info.replace(/\D/g, "")}`
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const unmaskedValue = info.replace(/\D/g, "");
    let isValid = true;

    if (!isValidCPF(unmaskedValue)) {
      setError("CPF inválido!");
      isValid = false;
    }

    if (isValid) {
      try {
        setErrorData("");
        setPdfURL("");
        setData(null);
        setIsLoading(true);
        setError("");
        setErrorQuery("");
        const { data: dados } = await fetchInfo();
        setData(dados.data.consultaCredito.dadosCadastrais);
      } catch (error) {
        setErrorQuery(
          typeof error?.response?.data?.message !== "object"
            ? error?.response?.data?.message
            : "Algum erro ocorreu com sua consulta"
        );
      } finally {
        setIsLoading(false);
      }
    } else {
      setData(null);
    }
  };

  return (
    <>
      <div className="text-lg font-semibold mb-3 title-syncx">
        {query?.name}
      </div>
      <CardQueryDescription
        title="Informações da consulta"
        infos={[
          "Dados Cadastrais",
          "Dados profissionais",
          "Endereços",
          "E-mails",
          "Telefones",
        ]}
        price={selectedPrice}
      >
        <form
          className="flex flex-col gap-2 bg-white p-2 mt-2 rounded-lg shadow-md w-fit"
          onSubmit={handleSubmit}
        >
          <div className="flex gap-2 items-center">
            <div className="flex items-center gap-2">
              <div className="block">
                <Label htmlFor="info" value="CPF:" />
              </div>
              <TextInput
                id="info"
                type="text"
                required
                onChange={handleChange}
                maxLength="14"
                value={info}
              />
            </div>
            <Button
              className=" shadow-lg primary flex items-center justify-center"
              type="submit"
              isProcessing={isLoading}
              processingSpinner={<CgSpinner className="h-5 w-5 animate-spin" />}
              style={{ height: "43px" }}
            >
              <FaSearch />
            </Button>
            <div>
              {!errorData && data && (
                <Button
                  className={`w-fit shadow-lg secondary ${
                    data && !isLoading ? "block" : "hidden"
                  }`}
                  isProcessing={pdfURL === ""}
                  disabled={pdfURL === ""}
                  onClick={() => handleClickDownloadPDF()}
                >
                  Salvar como PDF
                </Button>
              )}
            </div>
          </div>
          {error && <small className="text-red-600">{error}</small>}
        </form>
      </CardQueryDescription>
      {errorQuery ? (
        <Alert color="failure" icon={HiInformationCircle}>
          <span className="font-medium">{errorQuery}</span>
        </Alert>
      ) : (
        <div
          className={`${data || isLoading ? "block" : "hidden"}`}
          ref={componentRef}
        >
          {!isLoading ? (
            <Card className="relative overflow-hidden p-5 card-pdf">
              <HeaderQuery title={query?.name} />
              <TitleStripe>Dados Cadastrais</TitleStripe>
              <div className="flex gap-16">
                <div className="text-base flex-1">
                  <div className="mb-3">
                    <span className="font-semibold">Nome completo: </span>
                    {data?.nome}
                  </div>

                  <div className="mb-3">
                    <span className="font-semibold">Nome da mãe: </span>
                    {data?.nomeMae || "Indisponível"}
                  </div>
                  <div className="mb-3">
                    <span className="font-semibold">Data de nascimento: </span>
                    {data?.dataNascimento || "Indisponível"}
                  </div>
                </div>
                <div className="text-base flex-1">
                  <div className="mb-3">
                    <span className="font-semibold">CPF: </span>
                    {data?.cpf}
                  </div>
                  <div className="mb-3 flex gap-2">
                    <span className="font-semibold">Situação: </span>
                    <div
                      className={`${
                        data?.situacao?.toUpperCase() === "REGULAR"
                          ? "bg-primary"
                          : "bg-red-500"
                      } px-2 rounded-md text-white font-semibold`}
                    >
                      {data?.situacao || "Indisponível"}
                    </div>
                  </div>
                  <div className="mb-3">
                    <span className="font-semibold">Sexo: </span>
                    {(data?.sexo === "M" ? "Masculino" : "Feminino") ||
                      "Indisponível"}
                  </div>
                </div>
              </div>

              <div>
                <TitleStripe>Endereços</TitleStripe>
                {data?.enderecos?.length > 0 ? (
                  <Enderecos enderecos={data?.enderecos} />
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe>Histórico E-mails</TitleStripe>
                {data?.emails?.length > 0 ? (
                  <Emails emails={data?.emails} />
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe>Histórico Telefones</TitleStripe>
                {data?.telefones?.length > 0 ? (
                  <Telefones telefones={data?.telefones} />
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe>Histórico Celulares</TitleStripe>
                {data?.celulares?.length > 0 ? (
                  <Telefones telefones={data?.celulares} />
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div className="border border-gray-200 p-3 text-xs text-gray-600 mt-10 text-justify bg-gray-100">
                <div className="font-semibold underline mb-1">
                  Informações Importantes
                </div>
                <p>
                  Confidencialidade e Uso das Informações As informações
                  contidas nesta mensagem são confidenciais e devem ser
                  utilizadas exclusivamente para a orientação das transações
                  comerciais relacionadas ao CNPJ: 28.932.263/0001-58. Qualquer
                  uso em desacordo com a legislação vigente, incluindo a Lei
                  Geral de Proteção de Dados (Lei 13.709/18 - LGPD), poderá
                  acarretar responsabilidade civil e criminal pelos danos
                  causados a terceiros. A responsabilidade da Carro Positivo se
                  limita à transmissão fiel dos dados obtidos de veículos
                  automotores registrados em bases de dados públicas e privadas.
                  As informações exibidas nesta consulta refletem o conteúdo
                  disponível na data e hora da pesquisa e são de
                  responsabilidade do CNPJ: 28.932.263/0001-58. Em caso de
                  dúvida ou divergência, recomenda-se consultar diretamente o
                  órgão competente. O CNPJ 28.932.263/0001-58 declara estar
                  ciente de que todas as informações fornecidas nesta consulta
                  têm o objetivo de auxiliar na verificação da procedência do
                  veículo, servindo apenas como ferramenta de análise
                  preliminar, e não como a única base para tomada de decisão em
                  processos de vistoria ou comercialização do veículo. É
                  importante destacar que o levantamento de informações por meio
                  de consulta eletrônica não substitui a consulta ao órgão
                  oficial competente, especialmente porque os dados podem ser
                  alterados, incluídos ou removidos em tempo real por órgãos
                  públicos, instituições financeiras e seguradoras. Veículos
                  cadastrados em outros estados podem apresentar vícios ocultos,
                  restrições, débitos ou bloqueios, que só serão identificados
                  no prontuário do veículo no momento da transferência efetiva
                  para o novo estado. As informações mencionadas são de uso
                  exclusivo do destinatário e estão protegidas por sigilo
                  contratual. Qualquer uso por terceiros ou para finalidades
                  distintas daquelas acordadas caracteriza ilícito civil,
                  invalidando a prova para efeitos processuais.
                </p>

                <div className="font-semibold mt-2 underline mb-1">Aviso</div>
                <p>
                  Ainda que empregando os melhores esforços, nossa empresa se
                  exime de qualquer responsabilidade pela eventual não inclusão
                  de algum registro devido a atraso ou falta de encaminhamento
                  dos dados pelos diversos provedores de informações
                  conveniados. Em caso de dúvidas, entre em contato com nosso
                  suporte.
                </p>
                <div className="font-semibold mt-2 underline mb-1">
                  Minuta de Declaração LGPD
                </div>
                <p>
                  Declaro que a utilização dos dados respeitará as finalidades e
                  procedimentos legalmente admitidos pela Lei Geral de Proteção
                  de Dados, conforme divulgado em minha página inicial de
                  acesso.
                </p>
              </div>
            </Card>
          ) : (
            <QueryLoading />
          )}
        </div>
      )}
    </>
  );
};

export default CadastralPFQuery;
